import { Fragment, useState, type FormEvent } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useRessource } from '../../hooks/useRessource';
import { ErreurApi, api } from '../../lib/apiClient';
import { Champ, Chargement, EnTetePage, Erreur, Infobulle, Modale } from '../../components/Elements';

interface Role {
  id: string;
  code: string;
  libelle: string;
  niveau: number;
  systeme: boolean;
  /** Role systeme dont la matrice a ete reglee ici : la migration ne le reinitialise plus. */
  personnalise: boolean;
  /** Habilitations en cours qui le portent — ce qui interdit sa suppression. */
  habilitations: number;
}

interface Permission {
  id: string;
  code: string;
  domaine: string;
  libelle: string;
}

interface Matrice {
  roles: Role[];
  permissions: Permission[];
  matrice: { role_code: string; permission_code: string; portee: string }[];
}

/**
 * Les quatre etats d'une case, dans l'ordre ou le clic les fait defiler.
 * `null` ferme l'acces ; les trois autres sont les portees de la base.
 */
const ETATS = [null, 'tout', 'propre', 'agrege'] as const;
type Etat = (typeof ETATS)[number];

const SYMBOLES: Record<string, { signe: string; titre: string; classe: string }> = {
  tout: { signe: '●', titre: 'Acces complet', classe: 'text-encre-900' },
  propre: { signe: '◐', titre: 'Restreint a ses propres donnees', classe: 'text-amber-600' },
  agrege: { signe: '◐', titre: 'Donnees agregees uniquement', classe: 'text-amber-600' },
};

const ROLE_INTOUCHABLE = 'super_admin';

/**
 * CDC M0.3 : la matrice permissions x roles. C'est la representation de
 * `role_permission` — le seul endroit ou se decide qui peut quoi (RG-35).
 *
 * Elle est REGLABLE depuis 0.37.0, sous la permission `role.modifier` :
 * on cree des roles, et on ajuste la colonne d'un role existant. Deux
 * choses a savoir, dites a l'ecran plutot qu'ici seulement :
 *
 *   - regler un role LIVRE avec la plateforme le detache des migrations,
 *     qui cesseraient sinon de respecter le reglage au prochain
 *     deploiement. Il cesse alors de recevoir les permissions ajoutees
 *     par les versions ulterieures — d'ou le retour au standard ;
 *   - le super admin ne se regle pas : il detient tout par construction,
 *     et c'est ce qui rend tout reglage malheureux rattrapable.
 */
export function RolesPage() {
  const { peut } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<Matrice>('/api/admin/roles'),
    [],
  );

  // Role en cours d'edition, et la colonne telle qu'elle est en train
  // d'etre reglee : tant qu'on n'enregistre pas, rien n'est envoye.
  const [editionId, setEditionId] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<Map<string, Etat>>(new Map());
  const [creation, setCreation] = useState(false);
  const [fiche, setFiche] = useState<Role | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  if (chargement) return <Chargement />;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees) return null;

  const modifiable = peut('role.modifier');
  const enEdition = donnees.roles.find((role) => role.id === editionId) ?? null;

  const accorde = new Map(
    donnees.matrice.map((ligne) => [`${ligne.role_code}|${ligne.permission_code}`, ligne.portee]),
  );

  const parDomaine = donnees.permissions.reduce<Record<string, Permission[]>>(
    (accumulateur, permission) => {
      (accumulateur[permission.domaine] ??= []).push(permission);
      return accumulateur;
    },
    {},
  );

  const ouvrirEdition = (role: Role) => {
    setErreurAction(null);
    setEditionId(role.id);
    setBrouillon(
      new Map(
        donnees.permissions.map((permission) => [
          permission.code,
          (accorde.get(`${role.code}|${permission.code}`) as Etat) ?? null,
        ]),
      ),
    );
  };

  const fermerEdition = () => {
    setEditionId(null);
    setBrouillon(new Map());
  };

  const basculer = (code: string) => {
    setBrouillon((precedent) => {
      const suivant = new Map(precedent);
      const rang = ETATS.indexOf(precedent.get(code) ?? null);
      suivant.set(code, ETATS[(rang + 1) % ETATS.length]!);
      return suivant;
    });
  };

  const modifications = enEdition
    ? donnees.permissions.filter(
        (permission) =>
          (brouillon.get(permission.code) ?? null) !==
          ((accorde.get(`${enEdition.code}|${permission.code}`) as Etat) ?? null),
      ).length
    : 0;

  const enregistrer = async () => {
    if (!enEdition) return;
    setErreurAction(null);
    setEnCours(true);
    try {
      await api.put(`/api/admin/roles/${enEdition.id}/permissions`, {
        permissions: [...brouillon.entries()]
          .filter(([, portee]) => portee !== null)
          .map(([permissionCode, portee]) => ({ permissionCode, portee })),
      });
      fermerEdition();
      recharger();
    } catch (cause) {
      setErreurAction(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const reinitialiser = async (role: Role) => {
    if (
      !window.confirm(
        `Revenir a la matrice d’origine du role « ${role.libelle} » ? ` +
          'Les ajustements faits ici seront perdus, et le role suivra de nouveau les mises a jour.',
      )
    ) {
      return;
    }
    setErreurAction(null);
    try {
      await api.post(`/api/admin/roles/${role.id}/reinitialisation`);
      fermerEdition();
      recharger();
    } catch (cause) {
      setErreurAction(cause instanceof ErreurApi ? cause.message : 'Retour impossible.');
    }
  };

  const supprimer = async (role: Role) => {
    if (!window.confirm(`Supprimer definitivement le role « ${role.libelle} » ?`)) return;
    setErreurAction(null);
    try {
      await api.delete(`/api/admin/roles/${role.id}`);
      setFiche(null);
      fermerEdition();
      recharger();
    } catch (cause) {
      setErreurAction(cause instanceof ErreurApi ? cause.message : 'Suppression impossible.');
    }
  };

  return (
    <>
      <EnTetePage
        titre="Roles et permissions"
        description="Les droits sont portes par des permissions nommees agregees en roles. Aucun controle d’acces ne s’ecrit ailleurs par comparaison sur un nom de role."
        actions={
          modifiable ? (
            <button type="button" className="bouton-principal" onClick={() => setCreation(true)}>
              Nouveau role
            </button>
          ) : undefined
        }
      />

      {erreurAction && (
        <p
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          role="alert"
        >
          {erreurAction}
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-4 text-xs text-encre-500">
        <span>
          <span className="text-encre-900">●</span> acces complet
        </span>
        <span>
          <span className="text-amber-600">◐</span> restreint (ses donnees, ou agrege)
        </span>
        <span>— aucun acces</span>
        {modifiable && !enEdition && (
          <span className="text-encre-400">
            Cliquez le nom d’un role pour ajuster sa colonne.
          </span>
        )}
      </div>

      {enEdition && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-sm text-amber-900">
            <strong>{enEdition.libelle}</strong> en modification — cliquez une case pour faire
            defiler ses etats.{' '}
            {modifications === 0
              ? 'Aucun changement pour l’instant.'
              : `${modifications} changement(s) en attente.`}
            {enEdition.systeme && (
              <span className="mt-1 block text-xs">
                Ce role est livre avec la plateforme : l’enregistrer le detache des mises a jour,
                qui cesseront de lui ajouter les permissions des versions suivantes.
              </span>
            )}
            {enEdition.habilitations > 0 && (
              <span className="mt-1 block text-xs">
                {enEdition.habilitations} habilitation(s) en cours porte(nt) ce role : le
                changement prend effet a leur requete suivante.
              </span>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" className="bouton-secondaire" onClick={fermerEdition}>
              Annuler
            </button>
            <button
              type="button"
              className="bouton-principal"
              onClick={() => void enregistrer()}
              disabled={enCours || modifications === 0}
            >
              {enCours ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      <div className="carte overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-encre-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-encre-500">
                Permission
              </th>
              {donnees.roles.map((role) => {
                const reglable = modifiable && role.code !== ROLE_INTOUCHABLE;
                return (
                  <th
                    key={role.id}
                    className={`px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide ${
                      role.id === editionId ? 'bg-amber-50 text-amber-900' : 'text-encre-500'
                    }`}
                  >
                    {reglable ? (
                      <Infobulle texte="Ajuster les permissions de ce role">
                        <button
                          type="button"
                          className="underline decoration-encre-300 underline-offset-4 hover:decoration-encre-600"
                          onClick={() => (role.id === editionId ? fermerEdition() : ouvrirEdition(role))}
                        >
                          {role.libelle}
                        </button>
                      </Infobulle>
                    ) : (
                      role.libelle
                    )}
                    <div className="mt-1 flex flex-col items-center gap-0.5 font-normal normal-case">
                      {!role.systeme && (
                        <span className="rounded bg-encre-100 px-1.5 py-0.5 text-[10px] text-encre-600">
                          personnalise
                        </span>
                      )}
                      {role.personnalise && (
                        <Infobulle
                          texte="Regle ici : ce role ne suit plus les mises a jour de la plateforme."
                          className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800"
                        >
                          ajuste
                        </Infobulle>
                      )}
                      {modifiable && (
                        <button
                          type="button"
                          className="text-[10px] text-encre-400 underline underline-offset-2 hover:text-encre-600"
                          onClick={() => setFiche(role)}
                        >
                          details
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Object.entries(parDomaine).map(([domaine, permissions]) => (
              <Fragment key={domaine}>
                <tr className="bg-encre-100">
                  <td
                    colSpan={donnees.roles.length + 1}
                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-encre-600"
                  >
                    {domaine}
                  </td>
                </tr>
                {permissions.map((permission) => (
                  <tr key={permission.id} className="border-t border-encre-100">
                    <td className="px-4 py-2">
                      <div>{permission.libelle}</div>
                      <div className="font-mono text-xs text-encre-400">{permission.code}</div>
                    </td>
                    {donnees.roles.map((role) => {
                      const edite = role.id === editionId;
                      const portee = edite
                        ? (brouillon.get(permission.code) ?? null)
                        : ((accorde.get(`${role.code}|${permission.code}`) as Etat) ?? null);
                      const initiale =
                        (accorde.get(`${role.code}|${permission.code}`) as Etat) ?? null;
                      const symbole = portee ? SYMBOLES[portee] : null;
                      const contenu = symbole ? (
                        <Infobulle texte={symbole.titre} className={symbole.classe}>
                          {symbole.signe}
                        </Infobulle>
                      ) : (
                        <span className="text-encre-300">—</span>
                      );

                      return (
                        <td
                          key={role.id}
                          className={`px-3 py-2 text-center ${
                            edite
                              ? portee !== initiale
                                ? 'bg-amber-100'
                                : 'bg-amber-50'
                              : ''
                          }`}
                        >
                          {edite ? (
                            <Infobulle texte={`${permission.code} — cliquer pour changer`} balise="div">
                              <button
                                type="button"
                                className="w-full rounded hover:bg-amber-200"
                                onClick={() => basculer(permission.code)}
                              >
                                {contenu}
                              </button>
                            </Infobulle>
                          ) : (
                            contenu
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-encre-400">
        L’Admin technique administre le contenant, pas le contenu : il n’a aucune permission sur les
        personnes ni sur le pilotage. Un depannage exigeant cet acces passe par une elevation
        motivee, a duree limitee et tracee. Le super admin, lui, detient tout par construction et ne
        se regle pas — c’est ce qui rend tout reglage rattrapable.
      </p>

      <FormulaireRole
        ouvert={creation}
        roles={donnees.roles}
        onFermer={() => setCreation(false)}
        onEnregistre={() => {
          setCreation(false);
          recharger();
        }}
      />

      <FicheRole
        role={fiche}
        onFermer={() => setFiche(null)}
        onEnregistre={() => {
          setFiche(null);
          recharger();
        }}
        onReinitialiser={reinitialiser}
        onSupprimer={supprimer}
      />
    </>
  );
}

/**
 * Creation d'un role. La reprise des permissions d'un role existant est
 * le cas courant — « comme Gestionnaire, mais sans les reglements » —, et
 * partir d'une page blanche sur quatre-vingts permissions n'aide personne.
 */
function FormulaireRole({
  ouvert,
  roles,
  onFermer,
  onEnregistre,
}: {
  ouvert: boolean;
  roles: Role[];
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [niveau, setNiveau] = useState(50);
  const [modele, setModele] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      let permissions: { permissionCode: string; portee: string }[] = [];

      if (modele) {
        const source = await api.get<Matrice>('/api/admin/roles');
        permissions = source.matrice
          .filter((ligne) => ligne.role_code === modele)
          .map((ligne) => ({ permissionCode: ligne.permission_code, portee: ligne.portee }));
      }

      await api.post('/api/admin/roles', { code, libelle, niveau, permissions });
      setCode('');
      setLibelle('');
      setModele('');
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Creation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Nouveau role" ouverte={ouvert} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <Champ libelle="Libelle" aide="Le nom affiche partout : « Comptable », « Referent chorale »…">
          <input
            className="champ"
            value={libelle}
            onChange={(e) => {
              setLibelle(e.target.value);
              // Le code se deduit du libelle tant qu'on n'y a pas touche :
              // c'est un identifiant technique, pas une saisie de plus.
              if (!code || code === codeDepuisLibelle(libelle)) {
                setCode(codeDepuisLibelle(e.target.value));
              }
            }}
            required
            autoFocus
          />
        </Champ>

        <Champ
          libelle="Code"
          aide="Identifiant technique, en minuscules. Il ne change plus une fois le role cree."
        >
          <input
            className="champ font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </Champ>

        <Champ libelle="Niveau" aide="Ordonne l’affichage des colonnes, du plus eleve au plus bas. Il ne donne aucun droit.">
          <input
            className="champ"
            type="number"
            min={0}
            max={200}
            value={niveau}
            onChange={(e) => setNiveau(Number(e.target.value))}
            required
          />
        </Champ>

        <Champ
          libelle="Partir des permissions de"
          aide="Le nouveau role recoit une copie de cette matrice, a ajuster ensuite. Laissez vide pour partir de zero."
        >
          <select className="champ" value={modele} onChange={(e) => setModele(e.target.value)}>
            <option value="">— Aucune permission —</option>
            {roles.map((role) => (
              <option key={role.id} value={role.code}>
                {role.libelle}
              </option>
            ))}
          </select>
        </Champ>

        {erreur && (
          <p
            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            role="alert"
          >
            {erreur}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="bouton-principal" disabled={enCours}>
            {enCours ? 'Creation…' : 'Creer le role'}
          </button>
        </div>
      </form>
    </Modale>
  );
}

/** Libelle et niveau d'un role existant, plus ses deux actions de sortie. */
function FicheRole({
  role,
  onFermer,
  onEnregistre,
  onReinitialiser,
  onSupprimer,
}: {
  role: Role | null;
  onFermer: () => void;
  onEnregistre: () => void;
  onReinitialiser: (role: Role) => void;
  onSupprimer: (role: Role) => void;
}) {
  const [libelle, setLibelle] = useState('');
  const [niveau, setNiveau] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [charge, setCharge] = useState<string | null>(null);

  // La modale n'est pas demontee entre deux ouvertures : on recharge les
  // champs quand elle change de role.
  if (role && charge !== role.id) {
    setCharge(role.id);
    setLibelle(role.libelle);
    setNiveau(role.niveau);
    setErreur(null);
  }

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!role) return;
    setErreur(null);
    setEnCours(true);
    try {
      await api.patch(`/api/admin/roles/${role.id}`, { libelle, niveau });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Modification impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre={role ? role.libelle : ''} ouverte={Boolean(role)} onFermer={onFermer}>
      {role && (
        <form onSubmit={soumettre} className="space-y-4">
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
            Code <span className="font-mono">{role.code}</span> — {role.habilitations}{' '}
            habilitation(s) en cours.{' '}
            {role.systeme
              ? 'Role livre avec la plateforme : son nom est fige, ses permissions se reglent dans la matrice.'
              : 'Role cree ici : il survit aux mises a jour, qui ne lui ajoutent donc aucune permission nouvelle.'}
          </p>

          <Champ libelle="Libelle">
            <input
              className="champ"
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              disabled={role.systeme}
              required
            />
          </Champ>

          <Champ libelle="Niveau" aide="Ordonne l’affichage des colonnes. Il ne donne aucun droit.">
            <input
              className="champ"
              type="number"
              min={0}
              max={200}
              value={niveau}
              onChange={(e) => setNiveau(Number(e.target.value))}
              required
            />
          </Champ>

          {erreur && (
            <p
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              role="alert"
            >
              {erreur}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <div className="flex gap-2">
              {role.personnalise && (
                <button
                  type="button"
                  className="bouton-secondaire"
                  onClick={() => onReinitialiser(role)}
                >
                  Revenir au role standard
                </button>
              )}
              {!role.systeme && (
                <button
                  type="button"
                  className="text-sm text-red-700 underline underline-offset-2 hover:text-red-900"
                  onClick={() => onSupprimer(role)}
                >
                  Supprimer
                </button>
              )}
            </div>
            <button type="submit" className="bouton-principal" disabled={enCours}>
              {enCours ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}
    </Modale>
  );
}

/** « Referent chorale » donne `referent_chorale`. */
function codeDepuisLibelle(libelle: string): string {
  return libelle
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}
